import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { usePageContext, useFeature } from '@hero/web-context';
import { useCurrentUser } from '@hero/hooks-shared';
import { AnalyticsOverview, OrderTable } from '@hero/web-components';
import { useMyAnalyticsOverviewApi, useMyOrdersListApi } from '@hero/api-hooks';
import { route } from '@hero/web-hooks';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  HandCoinsIcon,
  NewLoadsNavIcon,
} from '@hero/web-icons';
import { useOrderAgainConfig } from '@hero/web-hooks';

export default function DashboardHome() {
  const { t, i18n } = useTranslation(['dashboardHome', 'dashboard', 'm-financing']);
  const BannerArrow = i18n.dir() === 'rtl' ? ArrowLeftIcon : ArrowRightIcon;
  const { setPageName } = usePageContext();
  const { name, org, isDriver } = useCurrentUser();

  // Mirrors `MyOrdersPage` so the home-page active-orders table exposes
  // the same "Order Again" affordance via the shared `useOrderAgainConfig`
  // hook (ApiProvider defaults — no per-role SDK wiring).
  const orderAgainConfig = useOrderAgainConfig();

  // Role-neutral, lens-aware analytics. No lens = tenant-wide ("every
  // Order this org is on"), which preserves the numbers the dashboard
  // shipped under the legacy `useAnalyticsCarrierOverviewApi`. The
  // carrier-shape blocks (earnings, utilization, drivers) are still
  // returned because the backend dispatches the default-lens case to
  // the carrier overview service. See `apps/backend/.../analytics.controller.ts`.
  const { data: overviewData, isLoading: isOverviewLoading } = useMyAnalyticsOverviewApi();
  // Active orders panel mirrors `/my-orders` exactly — same tenant
  // scope, no role lens — so the home table and the
  // "Status: Active" filter on `/my-orders` return the same set. The
  // analytics tiles on `<AnalyticsOverview />` were also moved off
  // the `as_carrier` lens so the table count and the "Active" tile
  // agree (see `analytics.carrier.service.ts`). Earnings is the only
  // metric that still attributes per-viewer, and it does so at the
  // reducer step (`getViewerSellerAmount`) rather than narrowing the
  // DB query.
  const {
    data: activeOrders,
    isLoading: isOrdersLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
  } = useMyOrdersListApi({
    unifiedStatus: 'any_active_status',
  });

  const { isEnabled: canBrowsePublishedOrders } = useFeature('order_module', 'sub_published_orders');
  const { isEnabled: canSeeMyOrders } = useFeature('order_module', 'sub_my_orders');
  const { isEnabled: canSeeFinancingBanner } = useFeature(
    'financing_module',
    'sub_home_banner',
  );

  useEffect(() => {
    setPageName(t('dashboardHome:Home.title'));
  }, [setPageName, t]);

  const getWelcomeName = () => {
    if (!isDriver && org?.primaryContact?.name) {
      return org.primaryContact.name;
    }
    return '';
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold leading-normal text-black">
            {t('dashboardHome:Home.welcome')} {getWelcomeName()}
            {isDriver && name}
          </h2>
          <p className="text-gray-500 body1">{t('dashboardHome:Home.subtitle')}</p>
        </div>
      </div>

      <AnalyticsOverview
        data={overviewData}
        displayForUser="carrier"
        isLoading={isOverviewLoading}
      />

      {canBrowsePublishedOrders && (
        <>
        <style>{`
            @keyframes banner-enter {
              0% { opacity: 0; transform: translateY(16px) scale(0.96); }
              50% { opacity: 1; transform: translateY(-3px) scale(1.008); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            @keyframes banner-gradient {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            @keyframes arrow-nudge-ltr {
              0%, 60%, 100% { transform: translateX(0); }
              30% { transform: translateX(5px); }
            }
            @keyframes arrow-nudge-rtl {
              0%, 60%, 100% { transform: translateX(0); }
              30% { transform: translateX(-5px); }
            }
            @keyframes circle-float {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(-6px, 6px) scale(1.08); }
            }
            @keyframes circle-float-2 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(8px, -4px) scale(1.05); }
            }
          `}</style>
          <Link
            to={route('new_loads')}
            className="group block mt-6"
            style={{ animation: 'banner-enter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both' }}
          >
            <div
              className="relative flex items-center gap-4 overflow-hidden rounded-xl border border-primary-200/60 p-5 transition-all duration-300 hover:shadow-xl hover:scale-[1.015] hover:border-primary-300"
              style={{
                background: 'linear-gradient(135deg, #DEE6FB, #C5D6F8, #9CBBF4, #C5D6F8, #DEE6FB)',
                backgroundSize: '300% 300%',
                animation: 'banner-gradient 8s ease-in-out infinite',
              }}
            >
              {/* Decorative floating circles */}
              <div
                className="pointer-events-none absolute -top-10 -end-10 size-36 rounded-full bg-primary-400/10"
                style={{ animation: 'circle-float 7s ease-in-out infinite' }}
              />
              <div
                className="pointer-events-none absolute -bottom-8 -start-8 size-28 rounded-full bg-primary-500/8"
                style={{ animation: 'circle-float-2 9s ease-in-out infinite' }}
              />
              <div className="pointer-events-none absolute top-1/2 end-1/3 size-16 -translate-y-1/2 rounded-full bg-primary-300/10" />

              {/* Icon with live dot */}
              <div className="relative flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/90 shadow-sm backdrop-blur-sm ring-1 ring-primary-200/60 transition-all duration-300 group-hover:bg-white group-hover:shadow-md group-hover:scale-110 group-hover:ring-primary-300">
                <NewLoadsNavIcon className="size-5 text-primary-600" />
                {/* Live blinking dot */}
                <span className="absolute -top-1 -end-1 flex size-3">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary-400 opacity-75 [animation-duration:1.5s]" />
                  <span className="relative inline-flex size-3 rounded-full bg-primary-500" />
                </span>
              </div>

              {/* Text */}
              <div className="relative flex-1 min-w-0">
                <p className="font-bold text-primary-800 text-[15px]">
                  {t('dashboardHome:Home.new_orders_banner')}
                </p>
                <p className="text-sm text-primary-600/80 mt-0.5">
                  {t('dashboardHome:Home.new_orders_subtitle')}
                </p>
              </div>

              {/* Arrow */}
              <div className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-white/70 shadow-sm ring-1 ring-primary-200/40 transition-all duration-300 group-hover:bg-white group-hover:shadow-md group-hover:ring-primary-300">
                <BannerArrow
                  className="size-4 text-primary-600"
                  style={{ animation: `${i18n.dir() === 'rtl' ? 'arrow-nudge-rtl' : 'arrow-nudge-ltr'} 2s ease-in-out infinite` }}
                />
              </div>
            </div>
          </Link>
      </>
      )}

      {/*
        Embedded financing entry point. Sits below the published-orders
        banner so the carrier sees both opportunities at the top of
        their dashboard. Visually mirrors the new-loads banner but
        with the emerald palette that the /financing landing uses.
        Gated by `financing_module.sub_home_banner` so we can pull
        just the home-page advert without disabling the whole
        `/financing` module.
      */}
      {canSeeFinancingBanner && (
      <Link
        to={route('financing')}
        className="group block mt-4"
        style={{ animation: 'banner-enter 0.7s cubic-bezier(0.22, 1, 0.36, 1) both' }}
      >
        <div
          className="relative flex items-center gap-4 overflow-hidden rounded-xl border border-emerald-200/60 p-5 transition-all duration-300 hover:shadow-xl hover:scale-[1.015] hover:border-emerald-300"
          style={{
            background: 'linear-gradient(135deg, #ECFDF5, #D1FAE5, #A7F3D0, #D1FAE5, #ECFDF5)',
            backgroundSize: '300% 300%',
            animation: 'banner-gradient 10s ease-in-out infinite',
          }}
        >
          {/* Decorative floating circles */}
          <div
            className="pointer-events-none absolute -top-10 -end-10 size-36 rounded-full bg-emerald-300/15"
            style={{ animation: 'circle-float 7s ease-in-out infinite' }}
          />
          <div
            className="pointer-events-none absolute -bottom-8 -start-8 size-28 rounded-full bg-emerald-400/10"
            style={{ animation: 'circle-float-2 9s ease-in-out infinite' }}
          />

          {/* Icon */}
          <div className="relative flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/90 shadow-sm backdrop-blur-sm ring-1 ring-emerald-200/60 transition-all duration-300 group-hover:bg-white group-hover:shadow-md group-hover:scale-110 group-hover:ring-emerald-300">
            <HandCoinsIcon className="size-5 text-emerald-700" />
          </div>

          {/* Text */}
          <div className="relative flex-1 min-w-0">
            <p className="font-bold text-emerald-900 text-[15px]">
              {t('m-financing:home_card.title')}
            </p>
            <p className="text-sm text-emerald-800/80 mt-0.5">
              {t('m-financing:home_card.description')}
            </p>
          </div>

          {/* Arrow */}
          <div className="relative flex size-8 shrink-0 items-center justify-center rounded-full bg-white/70 shadow-sm ring-1 ring-emerald-200/40 transition-all duration-300 group-hover:bg-white group-hover:shadow-md group-hover:ring-emerald-300">
            <BannerArrow
              className="size-4 text-emerald-700"
              style={{ animation: `${i18n.dir() === 'rtl' ? 'arrow-nudge-rtl' : 'arrow-nudge-ltr'} 2s ease-in-out infinite` }}
            />
          </div>
        </div>
      </Link>
      )}

      {canSeeMyOrders && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">
            {t('dashboardHome:Home.active_orders')}
          </h3>
          <OrderTable
            orders={activeOrders ?? []}
            isLoading={isOrdersLoading}
            displayForUser="carrier"
            hasDetailDrawer
            drawerProps={{ orderAgainConfig }}
            columns={[
              { key: 'ref' },
              { key: 'destinations' },
              { key: 'status' },
              { key: 'driver' },
              { key: 'price' },
            ]}
            onLoadMore={onLoadMore}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>
      )}
    </div>
  );
}
